// "Spot the Slip" — a calm error-correction practice surface.
//
// Each item shows ONE Spanish sentence with a single deliberate learner mistake.
// You read it, decide what's off, then reveal the fix: a word-level diff (red =
// what you'd have written, sage = the correction) plus a one-line "why" and an
// optional deep-link to the matching handbook guide.
//
// Deliberately NOT a game: no score, no streak, no timer. The only controls
// after a reveal are "Got it" (drop the card) and "Review again" (send it to the
// back of the queue). Empty queue → a quiet "all caught up" panel.
import { CORRECTIONS, CORRECTION_TAGS } from "../data/corrections.js";
import { navigate } from "../router/router.js";
import { escapeHtml } from "../ui/helpers.js";

const TAG_LABEL = Object.fromEntries(CORRECTION_TAGS.map((t) => [t.id, t.label]));

// ---- Word-level diff (LCS over whitespace tokens) -----------------------
// Returns an ordered op list: {t:"eq"|"del"|"ins", a?, b?}. "del" tokens are in
// the wrong sentence only; "ins" tokens are in the right sentence only.
function wordDiff(wrong, right) {
  const A = wrong.split(/\s+/).filter(Boolean);
  const B = right.split(/\s+/).filter(Boolean);
  const n = A.length;
  const m = B.length;
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = A[i] === B[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const ops = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (A[i] === B[j]) {
      ops.push({ t: "eq", a: A[i] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      ops.push({ t: "del", a: A[i] });
      i++;
    } else {
      ops.push({ t: "ins", b: B[j] });
      j++;
    }
  }
  while (i < n) ops.push({ t: "del", a: A[i++] });
  while (j < m) ops.push({ t: "ins", b: B[j++] });
  return ops;
}

// Wrong line: equal tokens plain, changed/removed tokens struck through in red.
function wrongLineHTML(ops) {
  return ops
    .filter((o) => o.t !== "ins")
    .map((o) =>
      o.t === "del"
        ? `<span class="bg-red-50 text-red-600 line-through decoration-red-400 rounded px-1">${escapeHtml(o.a)}</span>`
        : `<span>${escapeHtml(o.a)}</span>`
    )
    .join(" ");
}

// Right line: equal tokens plain, changed/added tokens highlighted in sage.
function rightLineHTML(ops) {
  return ops
    .filter((o) => o.t !== "del")
    .map((o) =>
      o.t === "ins"
        ? `<span class="bg-sage-100 text-sage-800 font-semibold rounded px-1">${escapeHtml(o.b)}</span>`
        : `<span>${escapeHtml(o.b)}</span>`
    )
    .join(" ");
}

export function correctionView(app, params = {}) {
  let tag = params.tag && TAG_LABEL[params.tag] ? params.tag : "";
  let queue = []; // working deck of item ids
  let revealed = false;

  function pool() {
    return CORRECTIONS.filter((c) => !tag || c.tag === tag);
  }

  function resetQueue() {
    queue = pool().map((c) => c.id);
    revealed = false;
  }

  function current() {
    return queue.length ? CORRECTIONS.find((c) => c.id === queue[0]) : null;
  }

  app.innerHTML = `
    <section>
      <div class="mb-5">
        <h2 class="text-2xl font-bold text-sage-800">Spot the Slip</h2>
        <p class="text-sage-600 text-sm mt-1 max-w-2xl">Each sentence hides one common mistake. Read it, decide what's off, then reveal the fix. No scores — just the slip, the correction, and the reason. Cards you send to <i>Review again</i> come back at the end.</p>
      </div>
      <div class="flex flex-wrap items-center gap-1.5 mb-5" id="tag-chips">
        <button class="tag-chip" data-tag="">All slips</button>
        ${CORRECTION_TAGS.map(
          (t) => `<button class="tag-chip" data-tag="${t.id}">${escapeHtml(t.label)}</button>`
        ).join("")}
      </div>
      <div id="corr-stage"></div>
    </section>`;

  const stage = app.querySelector("#corr-stage");

  function paintChips() {
    app.querySelectorAll(".tag-chip").forEach((b) => {
      const on = b.dataset.tag === tag;
      b.className = `tag-chip px-2.5 py-1 rounded-full text-xs font-medium border transition ${
        on ? "bg-sage-500 text-white border-sage-500" : "bg-white text-sage-600 border-sage-200 hover:border-sage-300"
      }`;
    });
  }

  function renderCard() {
    const item = current();
    if (!item) {
      renderDone();
      return;
    }
    const remaining = queue.length;
    const isNoChange = item.wrong === item.right;

    stage.innerHTML = `
      <div class="max-w-2xl">
        <div class="flex items-center justify-between mb-3">
          <span class="text-[11px] uppercase tracking-wide text-sage-500">${escapeHtml(TAG_LABEL[item.tag] || item.tag)}</span>
          <span class="text-xs text-sage-400">${remaining} card${remaining === 1 ? "" : "s"} left</span>
        </div>

        <div class="bg-white border border-sage-200 rounded-2xl p-5 sm:p-6 shadow-sm">
          <p class="text-xs text-sage-400 uppercase tracking-wide mb-2">Read this — what's the slip?</p>
          <p class="text-xl sm:text-2xl text-sage-800 leading-relaxed">${escapeHtml(item.wrong)}</p>

          <div id="reveal" class="hidden mt-5 pt-5 border-t border-sage-100"></div>

          <div id="card-actions" class="mt-5 flex flex-wrap gap-2"></div>
        </div>
      </div>`;

    const actions = stage.querySelector("#card-actions");
    const reveal = stage.querySelector("#reveal");

    function showReveal() {
      revealed = true;
      const ops = wordDiff(item.wrong, item.right);
      const slugLink = item.slug
        ? `<button id="guide-link" class="mt-3 text-xs font-medium text-sage-500 hover:text-sage-800">Read the full guide →</button>`
        : "";

      reveal.innerHTML = isNoChange
        ? `<div class="rounded-xl border border-sage-100 bg-sage-50/60 p-4">
             <p class="text-sm font-semibold text-sage-700 mb-1">No slip here — it's already correct. 🌱</p>
             <p class="text-lg text-sage-800">${escapeHtml(item.right)}</p>
           </div>
           <p class="text-sm text-sage-600 mt-3"><span class="font-semibold text-sage-700">Why:</span> ${escapeHtml(item.why)}</p>
           ${slugLink}`
        : `<div class="space-y-3">
             <div>
               <p class="text-[11px] uppercase tracking-wide text-red-400 mb-1">The slip</p>
               <p class="text-lg leading-relaxed text-sage-700">${wrongLineHTML(ops)}</p>
             </div>
             <div>
               <p class="text-[11px] uppercase tracking-wide text-sage-500 mb-1">The fix</p>
               <p class="text-lg leading-relaxed text-sage-800">${rightLineHTML(ops)}</p>
             </div>
           </div>
           <p class="text-sm text-sage-600 mt-3"><span class="font-semibold text-sage-700">Why:</span> ${escapeHtml(item.why)}</p>
           ${slugLink}`;
      reveal.classList.remove("hidden");

      const gl = reveal.querySelector("#guide-link");
      if (gl) gl.addEventListener("click", () => navigate("article", { slug: item.slug }));

      actions.innerHTML = `
        <button id="got-it" class="px-4 py-2 rounded-lg text-sm font-medium bg-sage-500 text-white hover:bg-sage-600 transition">Got it</button>
        <button id="again" class="px-4 py-2 rounded-lg text-sm font-medium bg-white text-sage-600 border border-sage-200 hover:border-sage-300 transition">Review again</button>`;

      actions.querySelector("#got-it").addEventListener("click", () => {
        queue.shift();
        revealed = false;
        renderCard();
      });
      actions.querySelector("#again").addEventListener("click", () => {
        const id = queue.shift();
        queue.push(id); // back of the line for another pass
        revealed = false;
        renderCard();
      });
    }

    if (revealed) {
      showReveal();
    } else {
      actions.innerHTML = `
        <button id="show" class="px-4 py-2 rounded-lg text-sm font-medium bg-sage-500 text-white hover:bg-sage-600 transition">Show correction</button>`;
      actions.querySelector("#show").addEventListener("click", showReveal);
    }
  }

  function renderDone() {
    const n = pool().length;
    stage.innerHTML = `
      <div class="max-w-2xl text-center py-12">
        <p class="text-4xl mb-3">🌿</p>
        <p class="text-lg font-semibold text-sage-800">All caught up</p>
        <p class="text-sage-600 text-sm mt-1">You worked through ${n} slip${n === 1 ? "" : "s"}${tag ? " in " + escapeHtml(TAG_LABEL[tag]) : ""}. Come back any time — repetition is the whole point.</p>
        <button id="restart" class="mt-5 px-4 py-2 rounded-lg text-sm font-medium bg-white text-sage-600 border border-sage-200 hover:border-sage-300 transition">Go through them again</button>
      </div>`;
    stage.querySelector("#restart").addEventListener("click", () => {
      resetQueue();
      renderCard();
    });
  }

  app.querySelectorAll(".tag-chip").forEach((b) =>
    b.addEventListener("click", () => {
      tag = b.dataset.tag;
      paintChips();
      resetQueue();
      renderCard();
    })
  );

  paintChips();
  resetQueue();
  renderCard();
}
